import "pixi.js/advanced-blend-modes";
import { buildStudio, vanSprite, furnitureSprite, lightMaps, T, windowShafts, type Mood, type StudioScene } from "@bd/art";
import { barks, C } from "@bd/content";
import { dateOf, floorSize, lots, money, plots, priceIndex, storageCost, type Company, type GameState } from "@bd/sim";
import { Application, Container, Graphics, Rectangle, Sprite, Text, TilingSprite } from "pixi.js";
import { route, companyAgentsKey, pickSeat, petTexture, randomFloorPoint, spawnPoint, staffSprite, type Agent, type Point, type Walkable } from "./people";
import { Dust } from "./dust";
import { t, tp, tx } from "../i18n";
import { groundTexture, TownLife, townInfo } from "./life";
import { HD2DFilter } from "./post";
import { freshTex, tex } from "./textures";

/** A studio day across game weeks: mostly daylight, a golden afternoon, dusk and a lamp-lit night. */
const DAY_CYCLE: Mood[] = ["day", "day", "day", "golden", "dusk", "night"];
/** how strongly lit windows glow at each time of day */
const EM_ALPHA: Record<Mood, number> = { day: 0.2, golden: 0.45, dusk: 0.75, night: 0.95 };

export interface Ghost {
  kind: "room" | "furniture";
  type: string;
  x: number; // tiles (room) or tile inside the floor (furniture)
  y: number;
  w: number;
  h: number;
  rot?: number;
  valid: boolean;
}

export interface Overlays {
  selectedRoom: string | null;
  ghost: Ghost | null;
  showBars: boolean;
}

/** The isometric studio: pixel world scaled by a whole number, lit, post-processed, with crisp overlays on top. */
export class StudioView {
  app: Application;
  world = new Container(); // pixel space, scaled by S
  fx = new Container(); // world + light overlays, gets the HD-2D filter
  sortLayer = new Container();
  emLayer = new Container();
  overlay = new Graphics();
  labels = new Container();
  post = new HD2DFilter();
  dust = new Dust(80);
  life = new TownLife();
  ground = new TilingSprite({ texture: groundTexture(), width: 16, height: 16 });
  scene: StudioScene | null = null;
  S = 4;
  cam = { x: 0, y: 0 }; // world pixel at the screen centre
  private sceneKey = "";
  private agentsKey = "";
  private agents: Agent[] = [];
  private walk: Walkable | null = null;
  private base: Sprite | null = null;
  private shade: Sprite | null = null;
  private glow: Sprite | null = null;
  private baseEm: Sprite | null = null;
  private rand = mulberry(7);
  private quality: "high" | "low" = "high";
  private mood: Mood = "day";
  over: Overlays = { selectedRoom: null, ghost: null, showBars: true };
  game: GameState | null = null;
  company: Company | null = null;
  private time = 0;
  private lastSeed = "";
  private bubbles: { text: Container; agent: string; until: number }[] = [];
  /** boxes riding a conveyor across each print shop while your own press runs */
  private belts: { roomId: string; boxes: Graphics[]; x0: number; x1: number; uy: number }[] = [];
  /** the print shop's van: pulls up at the kerb the week a print run lands, then drives off */
  private van: { sprite: Sprite; ux: number; want: boolean } | null = null;

  constructor(app: Application) {
    this.app = app;
    this.world.addChild(this.sortLayer);
    this.sortLayer.sortableChildren = true;
    this.fx.addChild(this.ground, this.world, this.life.weatherLayer);
    app.stage.addChild(this.fx, this.overlay, this.labels);
    app.ticker.add((t) => this.tick(t.deltaMS / 1000));
  }

  setQuality(q: "high" | "low") {
    this.quality = q;
    this.life.setLow(q === "low");
    this.applyFilters();
  }

  private applyFilters() {
    const { width, height } = this.app.screen;
    this.fx.filterArea = new Rectangle(0, 0, width, height);
    this.fx.filters = this.quality === "high" ? [this.post] : [];
  }

  /** Rebuild the scene when the layout changes; update people when staff change. */
  update(game: GameState, co: Company) {
    this.game = game;
    this.company = co;
    // the street outside: how famous you are, the year's cars, a queue in launch week
    this.life.setTown(townInfo(game, co));
    // a print run lands this week or next: the van is on its way
    if (this.van) this.van.want = co.products.some((p) => p.orders.some((o) => o.arriveWeek >= game.week && o.arriveWeek - game.week <= 1));
    const d = dateOf(game.week);
    const season = (["winter", "spring", "summer", "autumn"] as const)[d.quarter - 1];
    const key = JSON.stringify([co.level, season, d.year, co.annexes ?? [], co.rooms.map((r) => [r.id, r.type, r.x, r.y, r.w, r.h, r.door, r.furniture.map((f) => [f.id, f.kind, f.x, f.y, f.rot])])]);
    if (key !== this.sceneKey) {
      const before = this.scene ? `${this.scene.w}x${this.scene.h}` : "";
      this.sceneKey = key;
      const fl = floorSize(co);
      this.buildScene(fl.w, fl.h, co, season);
      // a new game, a loaded save or a move: frame the whole floor again
      if (before !== `${this.scene!.w}x${this.scene!.h}` || this.game?.seed !== this.lastSeed) this.fit();
      this.lastSeed = game.seed;
      this.agentsKey = "";
      // the floor changed: visitors leave (new ones drop in), everyone else finds their desk again
      for (const a of this.agents) if (a.kind === "visitor") a.sprite.destroy();
      this.agents = this.agents.filter((a) => a.kind !== "visitor");
      for (const a of this.agents) {
        a.seat = undefined;
        a.fresh = true;
      }
    }
    const ak = companyAgentsKey(co);
    if (ak !== this.agentsKey) {
      this.agentsKey = ak;
      this.syncAgents(co);
    }
    // seat assignment follows projects
    this.assignSeats(co);
  }

  private buildScene(tilesW: number, tilesH: number, co: Company, season: "winter" | "spring" | "summer" | "autumn") {
    const idx = priceIndex(this.game?.week ?? 0);
    const forSale = lots(co).filter((l) => !l.owned).map((l) => ({ ...l, price: money((C.annex[l.kind]?.cost ?? 0) * idx) }));
    const scene = buildStudio({ tilesW, tilesH, level: co.level, rooms: co.rooms, season, year: dateOf(this.game?.week ?? 0).year, plots: plots(co), forSale });
    this.ground.texture = groundTexture(season);
    this.scene = scene;
    this.walk = { rooms: co.rooms, tilesW, tilesH, seats: scene.seats };
    for (const s of [this.base, this.shade, this.glow, this.baseEm]) {
      if (!s) continue;
      const t = s.texture;
      s.destroy();
      t?.destroy(true);
    }
    // keep agents' sprites, rebuild everything else in the sort layer
    for (const c of [...this.sortLayer.children]) if (!this.agents.some((a) => a.sprite === c)) c.destroy();
    this.emLayer.removeChildren().forEach((c) => c.destroy());
    for (const t of this.chips.values()) t.destroy();
    this.chips.clear();
    this.base = new Sprite(freshTex(scene.base));
    this.world.addChildAt(this.base, 0);
    for (const s of scene.sprites) {
      const sp = new Sprite(tex(s.img));
      sp.position.set(s.px, s.py);
      sp.zIndex = s.depth;
      this.sortLayer.addChild(sp);
      if (s.em) {
        const e = new Sprite(tex(s.em));
        e.position.set(s.px, s.py);
        e.blendMode = "add";
        this.emLayer.addChild(e);
      }
    }
    this.baseEm = new Sprite(freshTex(scene.baseEm));
    this.baseEm.blendMode = "add";
    this.baseEm.alpha = 0.6;
    const shafts = windowShafts(scene);
    this.shafts = shafts;
    const maps = lightMaps(scene.w, scene.h, scene.lights, shafts, this.mood);
    this.dust.setShafts(shafts.map((s) => s.pts), this.app.renderer);
    this.shade = new Sprite(freshTex(maps.shade));
    this.shade.blendMode = "multiply";
    this.glow = new Sprite(freshTex(maps.glow));
    this.glow.blendMode = "add";
    this.baseEm.alpha = EM_ALPHA[this.mood];
    // the town going about its day: people and traffic (busier as you get famous), birds, clouds, the season's weather
    this.life.build(scene, this.sortLayer, season, this.game?.week ?? 0);
    this.belts = [];
    for (const r of this.walk?.rooms ?? []) {
      if (r.type !== "print_shop") continue;
      const boxes: Graphics[] = [];
      for (let i = 0; i < 4; i++) {
        const g = new Graphics().rect(-3, -4, 6, 4).fill(0xc89a60).rect(-3, -4, 6, 1).fill(0xe0b878).rect(-0.5, -4, 1, 4).fill(0x9a7040);
        g.visible = false;
        this.sortLayer.addChild(g);
        boxes.push(g);
      }
      this.belts.push({ roomId: r.id, boxes, x0: (r.x + 0.6) * T, x1: (r.x + r.w - 0.6) * T, uy: (r.y + r.h - 0.8) * T });
    }
    const col = this.company?.color ?? "#E85848";
    const v = vanSprite([parseInt(col.slice(1, 3), 16), parseInt(col.slice(3, 5), 16), parseInt(col.slice(5, 7), 16)]);
    const vs = new Sprite(tex(v.img));
    vs.pivot.set(v.ax, v.ay);
    vs.visible = false;
    this.sortLayer.addChild(vs);
    this.van = { sprite: vs, ux: scene.lanes[0].x1, want: this.van?.want ?? false };
    // order: base, sorted sprites, light (multiply), glow (add), emissive (add)
    this.world.removeChildren();
    this.world.addChild(this.base, this.sortLayer, this.shade, this.glow, this.baseEm, this.emLayer, this.life.cloudLayer, this.dust.layer);
  }

  /** Relight the studio for a time of day: only the light maps change, the scene stays. */
  setMood(m: Mood) {
    if (m === this.mood) return;
    this.mood = m;
    if (!this.scene || !this.shade || !this.glow) return;
    const maps = lightMaps(this.scene.w, this.scene.h, this.scene.lights, this.shafts, m);
    const oldS = this.shade.texture;
    const oldG = this.glow.texture;
    this.shade.texture = freshTex(maps.shade);
    this.glow.texture = freshTex(maps.glow);
    oldS.destroy(true);
    oldG.destroy(true);
    // lit windows (yours and the town's) glow in the evening, not at noon
    if (this.baseEm) this.baseEm.alpha = EM_ALPHA[m];
  }

  /** The studio's day: a game week runs from morning to night, but the light changes at most every 20 seconds. */
  private dayClock() {
    if (!this.game || !this.dayNight) return;
    if (this.time - this.lastMoodAt < 20) return;
    const phase = this.game.week % DAY_CYCLE.length;
    const want = DAY_CYCLE[phase];
    if (want !== this.mood) {
      this.lastMoodAt = this.time;
      this.setMood(want);
    }
  }
  private lastMoodAt = -999;
  private shafts: ReturnType<typeof windowShafts> = [];
  /** the day/night clock (Menu → graphics can turn it off) */
  dayNight = true;

  /** Choose a whole-number scale that keeps the floor big but on screen, and centre the camera. */
  fit() {
    if (!this.scene) return;
    const { width, height } = this.app.screen;
    const phone = width < 760;
    const fill = (q: { w: number; h: number }) => Math.min(width / q.w, height / q.h);
    // with buildings next door, frame them all (if the people stay readable); otherwise the main building
    // fills about two thirds of the screen, never cut off
    const annexes = this.company?.annexes?.length ?? 0;
    const campus = !phone && annexes > 0 && Math.floor(fill(this.scene.campus) * 0.95) >= 2;
    const b = campus ? this.scene.campus : this.scene.bounds;
    let S = campus ? Math.floor(fill(b) * 0.95) : Math.round(fill(b) * 0.68);
    S = Math.max(1, Math.min(6, S));
    while (S > 1 && (S * b.w > width * 0.96 || S * b.h > height * (phone ? 0.8 : 0.88))) S--;
    // a phone is too narrow for whole steps: half steps let the building fill the width
    if (phone) S = Math.max(1, Math.min(4, Math.floor(fill(b) * 0.94 * 2) / 2));
    this.S = S;
    // on a phone the tip card and clock sit over the top, so the building sits a little lower
    this.cam = { x: b.x + b.w / 2, y: b.y + b.h / 2 + (phone ? -20 : 6) };
    this.applyCamera();
  }

  applyCamera() {
    const { width, height } = this.app.screen;
    this.world.scale.set(this.S);
    this.world.position.set(Math.round(width / 2 - this.cam.x * this.S), Math.round(height / 2 - this.cam.y * this.S));
    // the town ground carries on under and past the map, lined up with its tiles
    this.ground.width = width;
    this.ground.height = height;
    this.ground.tileScale.set(this.S);
    if (this.scene) this.ground.tilePosition.set(this.world.position.x + this.scene.ox * this.S, this.world.position.y + this.scene.oy * this.S);
    this.applyFilters();
  }

  zoom(dir: number, at?: { x: number; y: number }) {
    const before = at ? this.screenToWorld(at.x, at.y) : null;
    this.S = Math.max(1, Math.min(8, this.S + dir));
    this.applyCamera();
    if (before && at) {
      const after = this.screenToWorld(at.x, at.y);
      this.cam.x += before.x - after.x;
      this.cam.y += before.y - after.y;
      this.applyCamera();
    }
  }

  /** Shift the camera without the pan limits (the title backdrop). */
  panBy(dx: number, dy: number) {
    this.cam.x += dx / this.S;
    this.cam.y += dy / this.S;
    this.applyCamera();
  }

  pan(dx: number, dy: number) {
    this.cam.x -= dx / this.S;
    this.cam.y -= dy / this.S;
    if (this.scene) {
      this.cam.x = Math.max(0, Math.min(this.scene.w, this.cam.x));
      this.cam.y = Math.max(0, Math.min(this.scene.h, this.cam.y));
    }
    this.applyCamera();
  }

  /** Bring a building next door into the middle of the screen. */
  focusLot(kind: string) {
    const lot = this.scene?.forSale.find((l) => l.kind === kind);
    if (!lot || !this.scene) return;
    const [px, py] = this.scene.P((lot.x + lot.w / 2) * T, (lot.y + lot.h / 2) * T, 10);
    this.cam = { x: px, y: py };
    this.applyCamera();
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - this.world.position.x) / this.S, y: (sy - this.world.position.y) / this.S };
  }

  /** Screen point -> floor tile (z = 0). */
  screenToTile(sx: number, sy: number): { tx: number; ty: number; ux: number; uy: number } | null {
    if (!this.scene) return null;
    const w = this.screenToWorld(sx, sy);
    const X = (w.x - this.scene.ox) / 2; // x - y
    const Y = w.y - this.scene.oy; // x + y
    const ux = (X + Y) / 2;
    const uy = (Y - X) / 2;
    return { tx: Math.floor(ux / T), ty: Math.floor(uy / T), ux, uy };
  }

  /** Unit coords -> screen pixels. */
  toScreen(ux: number, uy: number, uz = 0): { x: number; y: number } {
    const [px, py] = this.scene!.P(ux, uy, uz);
    return { x: this.world.position.x + px * this.S, y: this.world.position.y + py * this.S };
  }

  roomAt(tx: number, ty: number) {
    return this.company?.rooms.find((r) => tx >= r.x && tx < r.x + r.w && ty >= r.y && ty < r.y + r.h) ?? null;
  }

  // ------------------------------------------------------------------ people and pets

  private syncAgents(co: Company) {
    const keep = new Map(this.agents.map((a) => [a.id, a]));
    const next: Agent[] = [];
    co.staff.forEach((s, i) => {
      let a = keep.get(s.id);
      if (!a) {
        const sp = new Sprite(staffSprite(s, "front", false, 0));
        sp.anchor.set(0.5, 1);
        this.sortLayer.addChild(sp);
        a = { id: s.id, kind: "staff", staff: s, sprite: sp, pos: spawnPoint(this.walk!, i), path: [], sitting: false, timer: 8 + this.rand() * 40, frame: 0, frameT: 0, facing: "front", fresh: this.agents.length === 0 || !this.walk!.seats.length ? true : s.hiredWeek !== this.game?.week };
      }
      a.staff = s;
      keep.delete(s.id);
      next.push(a);
    });
    co.pets.forEach((p, i) => {
      let a = keep.get(p.id);
      if (!a) {
        const sp = new Sprite(petTexture(p, "sit", 0));
        sp.anchor.set(0.5, 1);
        this.sortLayer.addChild(sp);
        a = { id: p.id, kind: "pet", pet: p, sprite: sp, pos: randomFloorPoint(this.walk!, this.rand), path: [], sitting: false, timer: 3 + i, frame: 0, frameT: 0, facing: "front", sleeping: true };
      }
      keep.delete(p.id);
      next.push(a);
    });
    for (const a of keep.values()) {
      if (a.kind === "visitor") next.push(a);
      else a.sprite.destroy();
    }
    this.agents = next;
  }

  /** Everyone in today sits at their own desk, in the room that fits their job; people who are away aren't drawn. */
  private assignSeats(co: Company) {
    if (!this.walk || !this.game) return;
    const week = this.game.week;
    const taken = new Set<string>();
    for (const a of this.agents) {
      if (a.kind !== "staff" || !a.staff) continue;
      const away = (a.staff.awayUntil ?? 0) > week;
      a.sprite.visible = !away;
      if (away) {
        a.seat = undefined;
        continue;
      }
      if (a.seat && this.walk.seats.some((s) => s.furnitureId === a.seat!.furnitureId) && !taken.has(a.seat.furnitureId)) {
        taken.add(a.seat.furnitureId);
        continue;
      }
      a.seat = pickSeat(this.walk, a.staff, taken);
      if (!a.seat && a.fresh) {
        // no desk free (an old save): wait in one of the rooms, not in the corridor
        a.pos = randomFloorPoint(this.walk, this.rand);
        a.path = [];
      }
      if (a.seat) {
        taken.add(a.seat.furnitureId);
        if (a.fresh) {
          // just arrived on screen (new game, a load, a move): already at their desk
          a.pos = { x: a.seat.ux, y: a.seat.uy };
          a.path = [];
          a.sitting = true;
        } else {
          a.path = route(this.walk, a.pos, { x: a.seat.ux, y: a.seat.uy });
          a.sitting = false;
        }
      }
      a.fresh = false;
    }
    void co;
  }

  private tick(dt: number) {
    this.time += dt;
    this.post.u.uTime = this.time * 60;
    this.dust.layer.visible = this.quality === "high";
    if (this.quality === "high") this.dust.tick(dt, this.time);
    if (!this.scene || !this.walk) return;
    for (const a of this.agents) this.stepAgent(a, Math.min(dt, 0.1));
    this.life.tick(Math.min(dt, 0.1), this.app.screen.width, this.app.screen.height);
    this.idleChatter();
    this.dayClock();
    this.driveVan(Math.min(dt, 0.1));
    this.runBelts();
    this.visitors();
    this.drawOverlay();
  }

  private runBelts() {
    if (!this.scene || !this.company) return;
    const printing = this.company.products.some((p) => p.orders.some((o) => o.factory === "own_shop"));
    for (const b of this.belts) {
      const len = b.x1 - b.x0;
      b.boxes.forEach((g, i) => {
        g.visible = printing && len > 4;
        if (!g.visible) return;
        const ux = b.x0 + (((this.time * 3 + (i * len) / b.boxes.length) % len) + len) % len;
        const [px, py] = this.scene!.P(ux, b.uy, 3);
        g.position.set(Math.round(px), Math.round(py));
        g.zIndex = ux + b.uy + 0.5;
      });
    }
  }

  /** Who drops in: customers in your shop and café, testers in the lab while a game is being tested. */
  private visitors() {
    const w = this.walk;
    const co = this.company;
    if (!w || !co || !this.scene) return;
    if (Math.floor(this.time * 2) === Math.floor((this.time - 0.05) * 2)) return; // twice a second is plenty
    const testing = co.projects.some((p) => p.phase === "playtest");
    for (const r of w.rooms) {
      const want = r.type === "shop_front" || r.type === "game_cafe" ? 2 : r.type === "playtest_lab" && testing ? 3 : 0;
      const here = this.agents.filter((a) => a.kind === "visitor" && a.visit?.roomId === r.id && !a.visit.leaving).length;
      if (here >= want || this.rand() > 0.15) continue;
      const look = Math.floor(this.rand() * 64);
      const sp = new Sprite(staffSprite({ look } as never, "back", false, 0));
      sp.anchor.set(0.5, 1);
      this.sortLayer.addChild(sp);
      const start = spawnPoint(w, Math.floor(this.rand() * 6));
      const dest = { x: (r.x + 0.5 + this.rand() * (r.w - 1)) * T, y: (r.y + 0.5 + this.rand() * (r.h - 1)) * T };
      this.agents.push({ id: `v${Math.floor(this.time * 1000)}:${r.id}`, kind: "visitor", sprite: sp, pos: start, path: route(w, start, dest), sitting: false, timer: 4 + this.rand() * 6, frame: 0, frameT: 0, facing: "back", visit: { roomId: r.id, look, leaveAt: this.time + 25 + this.rand() * 35, leaving: false } });
    }
    // testers go home when the testing's done; anyone whose visit is over walks out and is gone
    for (const a of this.agents) {
      if (a.kind !== "visitor" || !a.visit) continue;
      const room = w.rooms.find((r) => r.id === a.visit!.roomId);
      if (!a.visit.leaving && (!room || this.time > a.visit.leaveAt || (room.type === "playtest_lab" && !testing))) {
        a.visit.leaving = true;
        a.path = route(w, a.pos, spawnPoint(w, 0));
      }
    }
    const gone = this.agents.filter((a) => a.kind === "visitor" && a.visit?.leaving && !a.path.length);
    for (const a of gone) a.sprite.destroy();
    if (gone.length) this.agents = this.agents.filter((a) => !gone.includes(a));
  }

  private driveVan(dt: number) {
    const v = this.van;
    if (!v || !this.scene) return;
    const lane = this.scene.lanes[0];
    const stop = this.scene.kerb.ux;
    if (v.want) {
      // drive in from the right and stop at the kerb
      if (!v.sprite.visible) {
        v.ux = Math.min(lane.x1, stop + 120);
        v.sprite.visible = true;
      }
      v.ux = Math.max(stop, v.ux - 14 * dt * (v.ux - stop > 20 ? 1 : 0.5));
    } else if (v.sprite.visible) {
      // done unloading: off down the road
      v.ux -= 16 * dt;
      if (v.ux < lane.x0) v.sprite.visible = false;
    }
    if (!v.sprite.visible) return;
    const [px, py] = this.scene.P(v.ux, this.scene.kerb.uy, 0);
    v.sprite.position.set(Math.round(px), Math.round(py));
    v.sprite.zIndex = v.ux + this.scene.kerb.uy + 6;
  }

  private stepAgent(a: Agent, dt: number) {
    const w = this.walk!;
    a.timer -= dt;
    if (!a.path.length) {
      if (a.kind === "staff") {
        if (a.seat) {
          const at = Math.hypot(a.pos.x - a.seat.ux, a.pos.y - a.seat.uy) < 0.8;
          if (!at) a.path = route(w, a.pos, { x: a.seat.ux, y: a.seat.uy });
          else {
            a.sitting = true;
            a.facing = a.seat.view;
            // stretch your legs now and then: coffee, the shelf, a chat
            if (a.timer <= 0) {
              a.timer = 25 + this.rand() * 40;
              // sometimes to the board or table in their own room, to think out loud with someone
              const room = w.rooms.find((r) => r.id === a.seat!.roomId) as (typeof w.rooms)[number] & { furniture?: { kind: string; x: number; y: number }[] };
              const board = room?.furniture?.find((f) => ["whiteboard", "design_table", "corkboard", "mural_canvas", "campaign_board"].includes(f.kind));
              if (room && board && this.rand() < 0.45) {
                a.sitting = false;
                a.path = [...route(w, a.pos, { x: (room.x + board.x + 0.5 + this.rand()) * T, y: (room.y + board.y + 1.5) * T })];
                return;
              }
              const brk = w.rooms.find((r) => r.type === "break_room") ?? w.rooms[Math.floor(this.rand() * w.rooms.length)];
              if (brk && this.rand() < 0.5) {
                a.sitting = false;
                const dest = { x: (brk.x + 0.5 + this.rand() * (brk.w - 1)) * T, y: (brk.y + 0.5 + this.rand() * (brk.h - 1)) * T };
                a.path = [...route(w, a.pos, dest)];
              }
            }
          }
        } else if (a.timer <= 0) {
          a.sitting = false;
          a.timer = 6 + this.rand() * 10;
          a.path = route(w, a.pos, randomFloorPoint(w, this.rand));
        }
      } else if (a.kind === "visitor") {
        if (a.timer <= 0 && a.visit && !a.visit.leaving) {
          a.timer = 4 + this.rand() * 8;
          const r = w.rooms.find((x) => x.id === a.visit!.roomId);
          if (r && this.rand() < 0.6) a.path = route(w, a.pos, { x: (r.x + 0.5 + this.rand() * (r.w - 1)) * T, y: (r.y + 0.5 + this.rand() * (r.h - 1)) * T });
        }
      } else if (a.timer <= 0) {
        // pets: long naps, short strolls
        a.sleeping = !a.sleeping;
        a.timer = a.sleeping ? 20 + this.rand() * 30 : 4 + this.rand() * 4;
        if (!a.sleeping) a.path = [randomFloorPoint(w, this.rand)];
      }
    }
    if (a.path.length) {
      a.sitting = false;
      const t = a.path[0];
      const dx = t.x - a.pos.x;
      const dy = t.y - a.pos.y;
      const dist = Math.hypot(dx, dy);
      const speed = a.kind === "pet" ? 5 : 7;
      if (dist < speed * dt) {
        a.pos = { ...t };
        a.path.shift();
      } else {
        a.pos = { x: a.pos.x + (dx / dist) * speed * dt, y: a.pos.y + (dy / dist) * speed * dt };
        a.facing = dx + dy > 0 ? "front" : "back";
      }
      a.frameT += dt;
      if (a.frameT > 0.22) {
        a.frameT = 0;
        a.frame ^= 1;
      }
    } else a.frame = 0;
    const [px, py] = this.scene!.P(a.pos.x, a.pos.y, 0);
    a.sprite.position.set(Math.round(px), Math.round(py) + (a.sitting ? -1 : 1));
    a.sprite.zIndex = a.pos.x + a.pos.y + (a.sitting ? 0.6 : 0.2);
    if (a.kind === "staff" && a.staff) a.sprite.texture = staffSprite(a.staff, a.facing, a.sitting, a.path.length ? a.frame : 0);
    else if (a.kind === "visitor" && a.visit) a.sprite.texture = staffSprite({ look: a.visit.look } as never, a.facing, false, a.path.length ? a.frame : 0);
    else if (a.pet) {
      a.sprite.texture = petTexture(a.pet, a.path.length ? "walk" : a.sleeping ? "sleep" : "sit", a.frame);
      a.sprite.scale.x = a.facing === "back" ? -1 : 1;
    }
  }

  /** Little "!" and progress bars over heads, selection and build ghosts: crisp, at screen resolution. */
  /**
   * What each person needs, at a glance: amber "!" nothing to work on, blue "z" burning out, red frown unhappy,
   * gold heart just shipped a hit. Hidden when zoomed far out.
   */
  private drawStatus(g: Graphics) {
    if (!this.game || this.S < 2) return;
    const week = this.game.week;
    const hit = this.company!.products.some((p) => p.launched && p.score >= 8 && week - p.releaseWeek < 4);
    const r = Math.max(4, this.S * 1.6);
    for (const a of this.agents) {
      if (a.kind !== "staff" || !a.staff || !a.sprite.visible) continue;
      const s = a.staff;
      const kind = s.burnout > 70 ? "z" : s.morale < 35 ? "sad" : !s.projectId && !s.training && (s.idleWeeks ?? 0) > 1 ? "idle" : hit ? "heart" : null;
      if (!kind) continue;
      const p = this.toScreen(a.pos.x, a.pos.y);
      const x = p.x;
      const y = p.y - a.sprite.height * this.S - r - 2;
      const color = kind === "z" ? 0x5a8cdc : kind === "sad" ? 0xc8443a : kind === "idle" ? 0xdea440 : 0xe85848;
      g.circle(x, y, r).fill({ color: 0xfaf6ee }).stroke({ width: Math.max(1.5, this.S * 0.5), color });
      if (kind === "idle") g.rect(x - r * 0.12, y - r * 0.55, r * 0.24, r * 0.65).fill(color).rect(x - r * 0.12, y + r * 0.25, r * 0.24, r * 0.24).fill(color);
      else if (kind === "z") g.moveTo(x - r * 0.4, y - r * 0.4).lineTo(x + r * 0.4, y - r * 0.4).lineTo(x - r * 0.4, y + r * 0.4).lineTo(x + r * 0.4, y + r * 0.4).stroke({ width: Math.max(1.5, this.S * 0.45), color });
      else if (kind === "sad") g.arc(x, y + r * 0.45, r * 0.4, Math.PI * 1.15, Math.PI * 1.85).stroke({ width: Math.max(1.5, this.S * 0.45), color }).circle(x - r * 0.3, y - r * 0.2, r * 0.1).fill(color).circle(x + r * 0.3, y - r * 0.2, r * 0.1).fill(color);
      else g.circle(x - r * 0.22, y - r * 0.1, r * 0.25).fill(color).circle(x + r * 0.22, y - r * 0.1, r * 0.25).fill(color).poly([x - r * 0.45, y, x + r * 0.45, y, x, y + r * 0.5]).fill(color);
    }
  }

  private drawOverlay() {
    const g = this.overlay;
    g.clear();
    if (!this.scene || !this.company) return;
    this.drawStatus(g);
    const sel = this.over.selectedRoom ? this.company.rooms.find((r) => r.id === this.over.selectedRoom) : null;
    if (sel) {
      const pts = [this.toScreen(sel.x * T, sel.y * T), this.toScreen((sel.x + sel.w) * T, sel.y * T), this.toScreen((sel.x + sel.w) * T, (sel.y + sel.h) * T), this.toScreen(sel.x * T, (sel.y + sel.h) * T)];
      g.poly(pts.flatMap((p) => [p.x, p.y])).fill({ color: 0xe85848, alpha: 0.12 }).stroke({ width: 3, color: 0xe85848 });
    }
    const gh = this.over.ghost;
    if (gh) {
      const color = gh.valid ? 0x22968c : 0xc8443a;
      if (gh.kind === "room") {
        const p = (x: number, y: number, z = 0) => this.toScreen(x * T, y * T, z);
        const base = [p(gh.x, gh.y), p(gh.x + gh.w, gh.y), p(gh.x + gh.w, gh.y + gh.h), p(gh.x, gh.y + gh.h)];
        const up = [p(gh.x, gh.y, 7), p(gh.x + gh.w, gh.y, 7), p(gh.x + gh.w, gh.y + gh.h, 7), p(gh.x, gh.y + gh.h, 7)];
        g.poly(base.flatMap((q) => [q.x, q.y])).fill({ color, alpha: 0.28 });
        for (let k = 1; k < gh.w; k++) g.moveTo(p(gh.x + k, gh.y).x, p(gh.x + k, gh.y).y).lineTo(p(gh.x + k, gh.y + gh.h).x, p(gh.x + k, gh.y + gh.h).y);
        for (let k = 1; k < gh.h; k++) g.moveTo(p(gh.x, gh.y + k).x, p(gh.x, gh.y + k).y).lineTo(p(gh.x + gh.w, gh.y + k).x, p(gh.x + gh.w, gh.y + k).y);
        g.stroke({ width: 1.5, color: 0xd6f0ea, alpha: 0.6 });
        for (let i = 0; i < 4; i++) g.moveTo(base[i].x, base[i].y).lineTo(up[i].x, up[i].y);
        g.poly(up.flatMap((q) => [q.x, q.y])).stroke({ width: 2.5, color: 0xd6f0ea, alpha: 0.9 });
        g.poly(base.flatMap((q) => [q.x, q.y])).stroke({ width: 3, color: gh.valid ? 0xffffff : color });
      } else if (gh.type === "door") {
        const p = (x: number, y: number) => this.toScreen(x * T, y * T);
        const q = [p(gh.x, gh.y), p(gh.x + 1, gh.y), p(gh.x + 1, gh.y + 1), p(gh.x, gh.y + 1)];
        g.poly(q.flatMap((v) => [v.x, v.y])).fill({ color: 0x22968c, alpha: 0.5 }).stroke({ width: 3, color: 0xffffff });
      } else {
        const room = this.roomAt(gh.x, gh.y);
        const sp = furnitureSprite(gh.type, gh.rot ?? 0, 0, dateOf(this.game?.week ?? 0).year);
        const p = (x: number, y: number) => this.toScreen(x * T, y * T);
        const base = [p(gh.x, gh.y), p(gh.x + sp.w, gh.y), p(gh.x + sp.w, gh.y + sp.h), p(gh.x, gh.y + sp.h)];
        g.poly(base.flatMap((q) => [q.x, q.y])).fill({ color, alpha: 0.35 }).stroke({ width: 2, color: 0xffffff });
        const rad = C.furniture[gh.type]?.fx.radius;
        if (rad && room) {
          const c = this.toScreen((gh.x + sp.w / 2) * T, (gh.y + sp.h / 2) * T);
          g.ellipse(c.x, c.y, rad * T * 2 * this.S, rad * T * this.S).fill({ color: 0x22968c, alpha: 0.12 }).stroke({ width: 2, color: 0x22968c, alpha: 0.8 });
        }
      }
    }
    if (this.over.showBars) {
      // one bar per project, floating over its team, with a "needs you" dot when it's waiting on you
      const teams = new Map<string, { x: number; y: number; n: number }>();
      for (const a of this.agents) {
        if (a.kind !== "staff" || !a.staff?.projectId || !a.sitting || !a.sprite.visible) continue;
        const t = teams.get(a.staff.projectId) ?? { x: 0, y: 0, n: 0 };
        t.x += a.pos.x;
        t.y += a.pos.y;
        t.n += 1;
        teams.set(a.staff.projectId, t);
      }
      for (const [pid, t] of teams) {
        const p = this.company.projects.find((x) => x.id === pid);
        if (!p) continue;
        const w = p.phase === "prototype" ? p.work.prototype : p.phase === "art" || p.phase === "rulebook" ? (p.work.art.done < p.work.art.need ? p.work.art : p.work.rulebook) : null;
        const prog = w ? w.done / w.need : p.phase === "ready" ? 1 : 0.5;
        const top = this.toScreen(t.x / t.n, t.y / t.n, 24);
        const bw = Math.max(40, 18 * this.S);
        g.roundRect(top.x - bw / 2 - 2, top.y - 8, bw + 4, 10, 5).fill({ color: 0xfaf6ee, alpha: 0.95 });
        g.roundRect(top.x - bw / 2, top.y - 6, bw * Math.max(0.05, Math.min(1, prog)), 6, 3).fill({ color: p.phase === "ready" ? 0xdea440 : 0x22968c });
        if (p.phase === "playtest" || p.phase === "ready") g.circle(top.x + bw / 2 + 12, top.y - 3, 8).fill({ color: 0xe85848 });
      }
    }
    this.updateChips();
    // bubbles follow their agents
    this.bubbles = this.bubbles.filter((b) => {
      const a = this.agents.find((x) => x.id === b.agent);
      if (!a || this.time > b.until) {
        b.text.destroy();
        return false;
      }
      const top = this.toScreen(a.pos.x, a.pos.y, 20);
      b.text.position.set(top.x, top.y - 16);
      return true;
    });
  }

  private lastBark = 0;
  private nextIdle = 12;

  /** Someone in the studio reacts to what just happened (a release, a hit, a level-up). One bubble at a time. */
  bark(kind: string, staffId?: string) {
    const lines = barks.events[kind];
    if (!lines?.length || this.time - this.lastBark < 2.5) return;
    const seated = this.agents.filter((a) => a.kind === "staff" && a.sprite.visible);
    const a = (staffId && seated.find((x) => x.id === staffId)) || seated[Math.floor(this.rand() * seated.length)];
    if (!a) return;
    this.lastBark = this.time;
    this.say(a.id, lines[Math.floor(this.rand() * lines.length)], 3.2);
  }

  /** Now and then someone mutters something in character. */
  private idleChatter() {
    if (this.time < this.nextIdle) return;
    this.nextIdle = this.time + 14 + this.rand() * 18;
    if (this.time - this.lastBark < 4) return;
    const seated = this.agents.filter((a) => a.kind === "staff" && a.sprite.visible && a.staff);
    const a = seated[Math.floor(this.rand() * seated.length)];
    if (!a?.staff) return;
    const trait = a.staff.traits.find((t) => barks.idle[t]);
    const pool = trait ? barks.idle[trait] : barks.generic;
    this.lastBark = this.time;
    this.say(a.id, pool[Math.floor(this.rand() * pool.length)], 2.8);
  }

  private chips = new Map<string, Container>();
  /** the room under the mouse, which gets its chip at any zoom */
  hoverRoom: string | null = null;
  private chipT = 0;

  /** A small label over each room: its name and what's going on in it right now. */
  private updateChips() {
    if (!this.scene || !this.company || !this.game) return;
    const co = this.company;
    const zoomed = this.S >= 3;
    for (const [id, t] of this.chips) if (!co.rooms.some((r) => r.id === id)) {
      t.destroy();
      this.chips.delete(id);
    }
    const refresh = this.time - this.chipT > 1;
    if (refresh) this.chipT = this.time;
    for (const r of co.rooms) {
      let chip = this.chips.get(r.id);
      if (!chip) {
        // the background goes in first, so the text draws on top of it
        chip = new Container();
        chip.addChild(new Graphics(), new Text({ text: "", style: { fontFamily: "Inter", fontSize: 12, fontWeight: "600", fill: 0x24263a } }));
        (chip.children[1] as Text).anchor.set(0.5, 1);
        this.labels.addChild(chip);
        this.chips.set(r.id, chip);
        this.chipT = 0;
      }
      const show = this.over.showBars && (zoomed || r.id === this.hoverRoom || r.id === this.over.selectedRoom);
      chip.visible = show;
      if (!show) continue;
      const t = chip.children[1] as Text;
      if (refresh || !t.text) {
        t.text = this.roomStatus(r);
        const bg = chip.children[0] as Graphics;
        bg.clear();
        bg.roundRect(-t.width / 2 - 8, -t.height - 3, t.width + 16, t.height + 6, 9).fill({ color: 0xfaf6ee, alpha: 0.94 }).stroke({ width: 1, color: 0xe0d6c4 });
      }
      const at = this.toScreen((r.x + r.w / 2) * T, r.y * T, 28);
      chip.position.set(Math.round(at.x), Math.round(at.y));
    }
  }

  private roomStatus(r: Company["rooms"][number]): string {
    const co = this.company!;
    const name = t(C.room[r.type]?.name ?? r.type);
    const inRoom = this.agents.filter((a) => a.kind === "staff" && a.sprite.visible && a.seat?.roomId === r.id);
    const projects = new Map<string, number>();
    for (const a of inRoom) if (a.staff?.projectId) projects.set(a.staff.projectId, (projects.get(a.staff.projectId) ?? 0) + 1);
    const top = [...projects.entries()].sort((a, b) => b[1] - a[1])[0];
    const p = top ? co.projects.find((x) => x.id === top[0]) : undefined;
    switch (r.type) {
      case "warehouse": {
        const st = storageCost(this.game!, co);
        const pct = Math.round((100 * st.pallets) / Math.max(1, st.capacity));
        return pct > 100 ? t("{name} · full, renting space outside", { name }) : t("{name} · {pct}% full", { name, pct });
      }
      case "print_shop": {
        const runs = co.products.flatMap((x) => x.orders).filter((o) => o.factory === "own_shop");
        return runs.length ? t("{name} · printing {n}", { name, n: runs.reduce((s2, o) => s2 + o.copies, 0).toLocaleString("en-US") }) : t("{name} · press idle", { name });
      }
      case "playtest_lab": {
        const ready = co.projects.find((x) => x.phase === "playtest");
        return ready ? t("{name} · testers here for {game}", { name, game: ready.design.name }) : t("{name} · waiting for a prototype", { name });
      }
      case "break_room": {
        const here = this.agents.filter((a) => a.kind === "staff" && !a.sitting && Math.floor(a.pos.x / T) >= r.x && Math.floor(a.pos.x / T) < r.x + r.w && Math.floor(a.pos.y / T) >= r.y && Math.floor(a.pos.y / T) < r.y + r.h).length;
        return here ? t("{name} · {n} on a break", { name, n: here }) : name;
      }
      case "archive": return `${name} · ${tp(co.stats.hits, "{n} hit on show", "{n} hits on show")}`;
      case "game_cafe": case "shop_front": {
        const n = this.agents.filter((a) => a.kind === "visitor" && a.visit?.roomId === r.id && !a.visit.leaving).length;
        return n ? `${name} · ${t("open")} · ${tp(n, "{n} customer", "{n} customers")}` : `${name} · ${t("open")}`;
      }
      default:
        if (p) {
          const w = p.phase === "prototype" ? p.work.prototype : p.phase === "art" || p.phase === "rulebook" ? { done: p.work.art.done + p.work.rulebook.done, need: p.work.art.need + p.work.rulebook.need } : null;
          return `${name} · ${p.design.name}${w ? ` ${Math.round((100 * w.done) / Math.max(1, w.need))}%` : p.phase === "ready" ? ` ${t("ready")}` : ""}`;
        }
        return inRoom.length ? t("{name} · {n} at their desks", { name, n: inRoom.length }) : name;
    }
  }

  /** Who is sitting in a room right now (staff ids). */
  staffInRoom(roomId: string): string[] {
    return this.agents.filter((a) => a.kind === "staff" && a.seat?.roomId === roomId).map((a) => a.id);
  }

  /** Drop every speech bubble (the news they were about has moved on). */
  clearBubbles() {
    for (const b of this.bubbles) b.text.destroy();
    this.bubbles = [];
    this.lastBark = 0;
  }

  say(agentId: string, text: string, seconds = 3) {
    const label = new Text({ text: tx(text), style: { fontFamily: "Inter", fontSize: 15, fontWeight: "600", fill: 0x24263a } });
    label.anchor.set(0.5, 1);
    const bg = new Graphics();
    const pad = 10;
    bg.roundRect(-label.width / 2 - pad, -label.height - 6, label.width + pad * 2, label.height + 12, 14).fill({ color: 0xfaf6ee }).stroke({ width: 2, color: 0x24263a });
    // background first, then the words on top
    const t = new Container();
    t.addChild(bg, label);
    this.labels.addChild(t);
    this.bubbles.push({ text: t, agent: agentId, until: this.time + seconds });
  }

  staffAgents(): { id: string; name: string }[] {
    return this.agents.filter((a) => a.kind === "staff").map((a) => ({ id: a.id, name: a.staff!.name }));
  }

  resize() {
    this.applyCamera();
  }

  /** Room centre in screen pixels (for anchoring HTML cards). */
  roomScreen(roomId: string): { x: number; y: number } | null {
    const r = this.company?.rooms.find((x) => x.id === roomId);
    if (!r || !this.scene) return null;
    return this.toScreen((r.x + r.w / 2) * T, (r.y + r.h / 2) * T, 0);
  }

  worldPoint(p: Point) {
    return this.toScreen(p.x, p.y);
  }
}

function mulberry(seed: number) {
  let s = seed;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
